import axios from 'axios';
import { normalizeNationalId } from '@/utils/paValidation';

function parseIprsDob(value) {
  if (!value || value === 'null') return '';
  try {
    const [month, day, year] = String(value).split(' ')[0].split('/');
    if (month && day && year) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  } catch { /* ignore */ }
  return '';
}

function namesMatch(iprsFirstName, cognitoFirstName) {
  const fetched = String(iprsFirstName || '').toUpperCase().trim();
  const expected = String(cognitoFirstName || '').toUpperCase().trim();
  if (!fetched || !expected) return false;
  return fetched.includes(expected) || expected.includes(fetched);
}

async function fetchIprsProfile(idNumber, cognitoFirstName = '') {
  const cleanId = normalizeNationalId(idNumber);
  if (!cleanId) {
    return {
      idNumber: cleanId,
      iprsUnavailable: true,
      idVerified: false,
      nameMismatch: false,
      iprs: null,
    };
  }

  const iprsRes = await axios.get('/customer/gis/policies/iprsdetail', {
    params: { idNumber: cleanId },
    timeout: 15000,
    _skipErrorRedirect: true,
    _showErrorToast: false,
    validateStatus: () => true,
  }).catch(() => null);

  const out = {
    idNumber: cleanId,
    iprsUnavailable: false,
    idVerified: false,
    nameMismatch: false,
    iprs: null,
  };

  const clean = (v) => (v && v !== 'null' ? v : '');

  if (iprsRes && iprsRes.status < 500) {
    const raw = iprsRes.data?.object;
    const data = Array.isArray(raw) ? raw[0] : raw;
    if (data) {
      out.iprs = {
        firstName: clean(data.firstName).toUpperCase(),
        otherName: clean(data.otherName).toUpperCase(),
        surname: clean(data.surname).toUpperCase(),
        fullName: [data.firstName, data.otherName, data.surname]
          .filter((v) => v && v !== 'null')
          .join(' ')
          .trim(),
        gender: clean(data.gender),
        citizenship: clean(data.citizenShip),
        occupation: clean(data.occupation),
        dateOfBirth: parseIprsDob(data.dateOfBirth),
      };
      if (cognitoFirstName) {
        if (namesMatch(out.iprs.firstName, cognitoFirstName)) {
          out.idVerified = true;
        } else {
          out.nameMismatch = true;
        }
      } else {
        out.idVerified = true;
      }
    } else {
      out.iprsUnavailable = true;
    }
  } else {
    out.iprsUnavailable = true;
  }

  return out;
}

/**
 * Verify customer identity via IPRS + KRA (parallel, independent).
 * @param {string} idNumber
 * @param {string} [cognitoFirstName] — Cognito given name for harvest check
 * @param {'customer'|'agent'} [channel='customer']
 */
async function verifyMotorIdentity(idNumber, cognitoFirstName = '', channel = 'customer') {
  const kraPath = channel === 'customer'
    ? '/customer/gis/policies/client-kra'
    : '/customer/gis/policies/kra-pin-check';

  const [iprsOut, kraRes] = await Promise.all([
    fetchIprsProfile(idNumber, cognitoFirstName),
    axios.get(kraPath, {
      params: { idNumber: normalizeNationalId(idNumber) },
      timeout: 15000,
      _skipErrorRedirect: true,
      _showErrorToast: false,
      validateStatus: () => true,
    }).catch(() => null),
  ]);

  const out = {
    ...iprsOut,
    kraVerified: false,
    kraPin: '',
    kraTaxpayerName: '',
  };

  if (kraRes && kraRes.status < 500) {
    const kra = kraRes.data?.object;
    if (kra && kra.ResponseCode === '30000') {
      out.kraVerified = true;
      out.kraPin = kra.TaxpayerPIN || '';
      out.kraTaxpayerName = kra.TaxpayerName || '';
    }
  }

  return out;
}

export { verifyMotorIdentity, fetchIprsProfile, parseIprsDob };
export default verifyMotorIdentity;
